import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Check, X, Star } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface Review {
  id: number;
  reviewerName: string;
  targetType: string;
  targetId: number;
  targetName: string | null;
  rating: number;
  reviewText: string;
  approved: boolean;
  createdAt: string;
}

type Tab = "all" | "order_store" | "business" | "trade";
type StatusFilter = "all" | "pending" | "approved";

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={13} className={i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-200 fill-slate-200"} />
      ))}
    </span>
  );
}

const TAB_LABELS: Record<Tab, string> = {
  all: "All",
  order_store: "Food & Local Ordering",
  business: "Business Directory",
  trade: "Trades",
};

export default function AdminReviews() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/reviews", { headers: authHeaders() })
      .then(r => r.json() as Promise<{ items: Review[] }>)
      .then(d => setReviews(d.items ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  function targetName(r: Review) {
    if (r.targetName) return r.targetName;
    if (r.targetType === "order_store") return `Shop #${r.targetId}`;
    if (r.targetType === "trade") return `Trade #${r.targetId}`;
    return `Business #${r.targetId}`;
  }

  const byTab = reviews.filter(r => tab === "all" || r.targetType === tab);
  const visible = byTab.filter(r => {
    if (statusFilter === "pending") return !r.approved;
    if (statusFilter === "approved") return r.approved;
    return true;
  });

  const countByTab = (t: Tab) => t === "all" ? reviews.length : reviews.filter(r => r.targetType === t).length;
  const pending = reviews.filter(r => !r.approved).length;

  async function setApproved(id: number, approved: boolean) {
    setActionLoading(id);
    try {
      const res = await fetch(`/api/reviews/${id}/approve`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ approved }),
      });
      if (!res.ok) throw new Error();
      setReviews(prev => prev.map(r => r.id === id ? { ...r, approved } : r));
      toast({ title: approved ? "Review approved" : "Review unapproved" });
    } catch {
      toast({ title: "Action failed", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  }

  async function deleteReview(id: number) {
    if (!confirm("Delete this review permanently?")) return;
    setActionLoading(id);
    try {
      const res = await fetch(`/api/admin/content/reviews/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error();
      setReviews(prev => prev.filter(r => r.id !== id));
      toast({ title: "Review deleted" });
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  }

  const activeTabs: Tab[] = ["all", "order_store", "business", "trade"].filter(
    t => t === "all" || countByTab(t as Tab) > 0
  ) as Tab[];

  return (
    <AdminLayout current="/admin/reviews">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Reviews</h1>
            <p className="text-sm text-slate-500 mt-1">Customer reviews across food ordering, business listings, and trade profiles.</p>
          </div>
          {pending > 0 && (
            <span className="text-sm font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700">
              {pending} pending approval
            </span>
          )}
        </div>

        {/* Type tabs */}
        <div className="flex gap-2 flex-wrap border-b border-slate-200 pb-3">
          {activeTabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                tab === t
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-slate-600 border-slate-200 hover:border-primary"
              }`}
            >
              {TAB_LABELS[t]} <span className="opacity-70">({countByTab(t)})</span>
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex gap-2">
          {(["all", "pending", "approved"] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize ${
                statusFilter === f
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading reviews…</p>
        ) : visible.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-2">
              <p className="text-3xl">⭐</p>
              <p className="font-semibold text-slate-700">No reviews found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {visible.map(r => (
              <Card key={r.id} className={!r.approved ? "border-amber-200 bg-amber-50 dark:bg-amber-900/20/30" : ""}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StarRating rating={r.rating} />
                        <span className="text-sm font-semibold text-slate-800">{r.reviewerName}</span>
                        <span className="text-xs text-slate-400">on</span>
                        <span className="text-xs font-medium text-primary">{targetName(r)}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          r.targetType === "order_store"
                            ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600"
                            : r.targetType === "trade"
                            ? "bg-purple-50 text-purple-600"
                            : "bg-slate-100 text-slate-500"
                        }`}>
                          {r.targetType === "order_store" ? "Food & Ordering" : r.targetType === "trade" ? "Trade" : "Business"}
                        </span>
                        {!r.approved && (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                            Pending
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700">{r.reviewText}</p>
                      <p className="text-xs text-slate-400">
                        {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!r.approved ? (
                        <Button size="sm" variant="outline"
                          className="h-8 text-green-600 border-green-200 hover:bg-green-50"
                          disabled={actionLoading === r.id}
                          onClick={() => setApproved(r.id, true)}
                        >
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline"
                          className="h-8 text-slate-500 border-slate-200 hover:bg-slate-50"
                          disabled={actionLoading === r.id}
                          onClick={() => setApproved(r.id, false)}
                        >
                          <X size={14} className="mr-1" /> Unapprove
                        </Button>
                      )}
                      <Button size="sm" variant="outline"
                        className="h-8 text-red-500 border-red-200 hover:bg-red-50"
                        disabled={actionLoading === r.id}
                        onClick={() => deleteReview(r.id)}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
